/**
 * Request deduplication utility to prevent multiple identical API calls
 */

// Map to track in-flight requests by their cache keys
const pendingRequests: Map<string, Promise<any>> = new Map();

/**
 * Executes a request function with deduplication logic
 * If multiple calls with the same key are made while a request is in-flight,
 * subsequent calls will await the result of the first call rather than making duplicate requests
 * 
 * @param key - Unique identifier for the request (typically cache key)
 * @param requestFn - The function that makes the actual API request
 * @param timeoutMs - Optional timeout in milliseconds
 * @returns Promise resolving to the response data
 */
export async function dedupRequest<T>(
  key: string, 
  requestFn: () => Promise<T>,
  timeoutMs = 30000
): Promise<T> {
  // If there's already a pending request for this key, return that promise
  if (pendingRequests.has(key)) {
    console.log(`Request deduplication: Using in-flight request for ${key}`);
    return pendingRequests.get(key) as Promise<T>;
  }

  // Create a promise that will resolve when the request completes
  // or reject if it times out or errors
  const requestPromise = new Promise<T>(async (resolve, reject) => {
    // Create timeout for the request
    const timeoutId = setTimeout(() => {
      pendingRequests.delete(key);
      reject(new Error(`Request timed out after ${timeoutMs}ms: ${key}`));
    }, timeoutMs);

    try {
      // Execute the actual request
      console.log(`Request deduplication: Starting new request for ${key}`);
      const result = await requestFn();
      // Clear timeout and resolve
      clearTimeout(timeoutId);
      resolve(result);
    } catch (error) {
      // Clear timeout and reject with the caught error
      clearTimeout(timeoutId);
      reject(error);
    } finally {
      // Cleanup - remove the request from pending map
      pendingRequests.delete(key);
      console.log(`Request deduplication: Completed and removed request for ${key}`);
    }
  });

  // Store the promise in the map
  pendingRequests.set(key, requestPromise);
  
  // Return the promise
  return requestPromise;
}

/**
 * Gets the count of pending requests (useful for debugging)
 */
export function getPendingRequestsCount(): number {
  return pendingRequests.size;
}

/**
 * Clears all pending requests (useful for testing or global error recovery)
 */
export function clearPendingRequests(): void {
  pendingRequests.clear();
}
